import { useState } from 'react';
import { CircleCheck, CircleX, MessageSquareReply, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PCMP_MKT_APPROVE_LABEL } from '../../constants/pcmpProposal.constants';
import type { PcmpMktApprovalKind } from '../../types/pcmpProposal.types';
import { PcmpMarketingApproveDialog } from './PcmpMarketingApproveDialog';
import { PcmpMarketingExplanationDialog } from './PcmpMarketingExplanationDialog';
import { PcmpMarketingRejectDialog } from './PcmpMarketingRejectDialog';
import { PcmpMktDisburseDialog } from './PcmpMktDisburseDialog';

type PcmpMarketingAction = 'approve' | 'reject' | 'explain' | 'disburse';

interface PcmpMarketingActionButtonsProps {
  proposalId: number;
  refNo: string;
  /** Set when the viewer may approve (or endorse) now. */
  approve: { kind: PcmpMktApprovalKind; levelNo: number | null } | null;
  canReject: boolean;
  /** From the details' capabilities; the reject dialog reads it itself when left out. */
  canRejectInstitute?: boolean | undefined;
  /** Set when an explanation may be submitted. */
  explanation: { misReasonLabel?: string | null | undefined } | null;
  /** The payment proof may be uploaded now (the Marketing payout). */
  canDisburse?: boolean;
  className?: string;
}

/**
 * Marketing's approve, reject, explanation and disburse buttons, each opening its
 * dialog. Which ones show is the caller's call, from the API's answer.
 */
export function PcmpMarketingActionButtons({
  proposalId,
  refNo,
  approve,
  canReject,
  canRejectInstitute,
  explanation,
  canDisburse = false,
  className,
}: PcmpMarketingActionButtonsProps) {
  const [open, setOpen] = useState<PcmpMarketingAction | null>(null);
  const close = () => setOpen(null);

  if (!approve && !canReject && !explanation && !canDisburse) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {canDisburse ? (
        <Button type="button" size="sm" onClick={() => setOpen('disburse')} className="gap-1.5">
          <Upload className="size-3.5" />
          Disburse (upload proof)
        </Button>
      ) : null}
      {approve ? (
        <Button type="button" size="sm" onClick={() => setOpen('approve')} className="gap-1.5">
          <CircleCheck className="size-3.5" />
          {PCMP_MKT_APPROVE_LABEL[approve.kind](approve.levelNo)}
        </Button>
      ) : null}
      {canReject ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen('reject')}
          className="gap-1.5 text-destructive hover:text-destructive"
        >
          <CircleX className="size-3.5" />
          Reject
        </Button>
      ) : null}
      {explanation ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen('explain')}
          className="gap-1.5"
        >
          <MessageSquareReply className="size-3.5" />
          Explain
        </Button>
      ) : null}

      {/* Mounted only while open, so each starts empty. */}
      {open === 'approve' && approve ? (
        <PcmpMarketingApproveDialog
          target={{ proposalId, refNo, kind: approve.kind, levelNo: approve.levelNo }}
          onClose={close}
        />
      ) : null}
      {open === 'reject' ? (
        <PcmpMarketingRejectDialog
          target={{ proposalId, refNo, canRejectInstitute }}
          onClose={close}
        />
      ) : null}
      {open === 'disburse' ? (
        <PcmpMktDisburseDialog target={{ proposalId, refNo }} onClose={close} />
      ) : null}
      {open === 'explain' && explanation ? (
        <PcmpMarketingExplanationDialog
          target={{ proposalId, refNo, misReasonLabel: explanation.misReasonLabel }}
          onClose={close}
        />
      ) : null}
    </div>
  );
}
